import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { refreshVerification, sendVerification, signOut } from '../AuthContext';

// The console gate for a password account whose address is not verified yet
// (RequireAuth). The rules refuse an unverified token, so there is nothing
// to show behind this screen — it sends the link, re-checks every few
// seconds (the link opens in another tab) and lets the user resend or sign
// out. Accounts that were marked verified by the one-time backfill pass
// straight through on the first refresh.
export default function VerifyEmail({ user, onVerified }) {
  const location = useLocation();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const checking = useRef(false);

  const next = location.pathname + location.search;

  // First render: the cached record may be stale (link already clicked,
  // backfill ran) — refresh before asking the user to do anything.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (await refreshVerification()) { if (alive && onVerified) onVerified(); return; }
      if (!alive || sent) return;
      try { await sendVerification(user, next); if (alive) setSent(true); } catch (e) { if (alive) setError(e.message); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Poll: the user clicks the link in their mail client; this tab notices.
  useEffect(() => {
    const t = setInterval(async () => {
      if (checking.current) return;
      checking.current = true;
      try { if (await refreshVerification()) { clearInterval(t); if (onVerified) onVerified(); } } finally { checking.current = false; }
    }, 4000);
    return () => clearInterval(t);
  }, [onVerified]);

  const resend = async () => {
    setBusy(true); setError('');
    try { await sendVerification(user, next); setSent(true); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="cs-auth-wrap">
      <div className="cs-modal" style={{ maxWidth: 440 }}>
        <h2 style={{ fontSize: 22, marginTop: 0 }}>Verify your email</h2>
        <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 0 }}>
          {sent ? 'We sent a link to ' : 'We are sending a link to '}<strong style={{ color: 'var(--text)' }}>{user.email}</strong>.
          Open it to confirm this address is yours — this page updates on its own.
        </p>
        {error && <div className="cs-empty" style={{ padding: 12, marginBottom: 14, color: 'var(--accent)' }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="cs-btn cs-btn-primary" disabled={busy} onClick={resend}>{busy ? 'Sending…' : 'Resend the link'}</button>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={() => refreshVerification().then((ok) => ok && onVerified && onVerified())}>I clicked it</button>
          <span style={{ flex: 1 }} />
          <button type="button" className="cs-btn cs-btn-ghost" onClick={() => signOut()} data-agent-noclick>Sign out</button>
        </div>
        <p style={{ marginTop: 14, fontSize: 12.5, color: 'var(--muted)' }}>
          Signing in with Google or Microsoft skips this step — those accounts arrive verified.
        </p>
      </div>
    </div>
  );
}
